/**
 * useAssignments - Hook for fetching assignments from Firestore
 *
 * Provides cached, type-safe access to assignment data with automatic
 * loading and error states via React Query.
 *
 * Usage:
 * ```tsx
 * const { assignments, isSuccess, error, refetch } = useAssignments();
 * const { assignments } = useChildAssignments(childId);   // едно дете — филтър в паметта
 * ```
 */

import { where, orderBy, QueryConstraint } from 'firebase/firestore';
import { useFirestoreQuery, schoolCollection } from './useFirestoreQuery';
import { queryKeys } from './queryKeys';
import { useAuth } from './useAuth';
import type { Assignment } from '@shared/types';

/**
 * 🔴 НЯМА `childId` и НЯМА `status` — нарочно, и това е гардът (39А §11).
 *
 * Заявката е `familyId == … · orderBy metadata.createdAt desc`. Всеки ДОПЪЛНИТЕЛЕН `where` иска
 * композитен индекс, който НЕ съществува нито на staging, нито на production, нито във
 * `firestore.indexes.json` (сверено 23.09). Без него заявката пада с `FAILED_PRECONDITION`, а при
 * паузиран повторен опит връща празно без грешка — детската страница казваше „училището не Ви е
 * изпратило" за ОТКАЗАНА декларация. Затова опциите не съществуват: повторна употреба НЕ СЕ
 * КОМПИЛИРА, вместо да чака да бъде забелязана.
 * ➜ Едно дете: `useChildAssignments(childId)` — същата семейна заявка + филтър в паметта.
 * ➜ Формата на заявката е пазена срещу истинския `firestore.indexes.json` в
 *   `__tests__/hooks/useAssignmentsIndexCoverage.test.tsx`.
 */
export interface UseAssignmentsOptions {
  /** Override schoolId (defaults to user's schoolId) */
  schoolId?: string;
  /** Override familyId (defaults to user's familyId) */
  familyId?: string;
  /** Whether to fetch (defaults to true when schoolId and familyId are available) */
  enabled?: boolean;
}

export interface UseAssignmentsResult {
  /** List of assignments */
  assignments: Assignment[];
  /** Loading state */
  isLoading: boolean;
  /**
   * Данните са УСПЕШНО заредени. 🔴 Решение „няма възлагания" се взима САМО при `true`:
   * паузирана заявка (react-query v5 паузира повторен опит при скрит таб или без мрежа) има
   * `isLoading: false` и празни данни, тоест `!isLoading` би прочел „не знам" като „няма".
   */
  isSuccess: boolean;
  /**
   * Error if query failed. `isNoServerResponse(error)` = сървърът не е достигнат — „не знам“, НЕ
   * „няма възлагания“ и НЕ „ето всички“ (`useFirestoreQuery` → `NoServerResponseError`).
   */
  error: Error | null;
  /**
   * В сесията няма `schoolId`/`familyId` — заявката изобщо не тръгва. `isSuccess` тогава е `true`
   * (празното е истината за ЗАЯВКАТА), но екранът трябва да го каже, не да мълчи (19.10, № 1116).
   */
  noFamily: boolean;
  /**
   * Обновяването падна, но данните от ПО-РАННО зареждане от сървъра (в тази сесия) са запазени
   * (`isRefetchError`). Екранът ги показва с бележка — НЕ ги сменя с панел за грешка: фоново
   * обновяване (фокус на прозореца) иначе затваря отворената форма за подпис (ревю кр.2, 20.10).
   * `error` е попълнен и в двата случая; „първото зареждане падна“ = `error && !refreshFailed`.
   */
  refreshFailed: boolean;
  /** Refetch function */
  refetch: () => void;
  /** Whether data is being refetched in background */
  isRefetching: boolean;
  /** Pending assignments count */
  pendingCount: number;
  /** Signed assignments count */
  signedCount: number;
  /** Declined assignments count */
  declinedCount: number;
}

/**
 * Hook for fetching family assignments
 */
export function useAssignments(options: UseAssignmentsOptions = {}): UseAssignmentsResult {
  const { customClaims, loading: authLoading } = useAuth();

  const schoolId = options.schoolId || customClaims?.schoolId;
  const familyId = options.familyId || customClaims?.familyId;

  // Build query constraints
  const constraints: QueryConstraint[] = [];

  if (familyId) {
    constraints.push(where('familyId', '==', familyId));
  }

  // Always order by creation date (newest first)
  constraints.push(orderBy('metadata.createdAt', 'desc'));

  const queryKey = queryKeys.school.assignments.byFamily(schoolId || '', familyId || '');

  const {
    data = [],
    isLoading,
    isSuccess,
    error,
    refetch,
    isRefetching,
    isRefetchError,
  } = useFirestoreQuery<Assignment>({
    queryKey,
    collectionPath: schoolCollection(schoolId || '', 'assignments'),
    constraints,
    enabled: options.enabled ?? (!!schoolId && !!familyId),
    idField: 'assignmentId',
    // Ключът е общ с таблото и детската страница — опцията е тук, в ЕДИНСТВЕНИЯ писач на ключа.
    requireServer: true,
  });

  /**
   * 🔴 Без семейство няма какво да се чете — празното е ИСТИНАТА, не „не знам" (гейт на §11, F3).
   * Изключената заявка никога не става `isSuccess`, тоест админ по адрес или родител, още невързан
   * със семейство, би гледал „Зарежда" завинаги. Решението чака `authLoading`: `AuthProvider` го
   * сваля едва след `getIdTokenResult()`, тоест докато claims пътуват, празно НЕ проблясва.
   */
  const безСемейство = options.enabled === undefined && !authLoading && !(schoolId && familyId);

  // Compute counts
  const pendingCount = data.filter((a) => a.status === 'pending').length;
  const signedCount = data.filter((a) => a.status === 'signed').length;
  const declinedCount = data.filter((a) => a.status === 'declined').length;

  return {
    assignments: data,
    isLoading,
    isSuccess: isSuccess || безСемейство,
    error,
    noFamily: безСемейство,
    refreshFailed: isRefetchError,
    refetch,
    isRefetching,
    pendingCount,
    signedCount,
    declinedCount,
  };
}

/**
 * Hook for fetching a single assignment by ID
 */
export function useAssignment(assignmentId: string | undefined) {
  const { customClaims } = useAuth();
  const schoolId = customClaims?.schoolId;

  const { data, isLoading, error, refetch } = useFirestoreQuery<Assignment>({
    queryKey: queryKeys.school.assignments.single(schoolId || '', assignmentId || ''),
    collectionPath: schoolCollection(schoolId || '', 'assignments'),
    constraints: [where('assignmentId', '==', assignmentId)],
    enabled: !!schoolId && !!assignmentId,
    idField: 'assignmentId',
  });

  return {
    assignment: data?.[0] || null,
    isLoading,
    error,
    refetch,
  };
}
